"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { EmblaCarouselType } from "embla-carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import useEmblaCarousel from "embla-carousel-react";
import type { CarouselProps } from "./types/carousel.types";

interface UseCarouselProps {
  autoScroll?: CarouselProps["autoScroll"];
  dragFree?: CarouselProps["dragFree"];
  loop: boolean;
  wheelGestures?: CarouselProps["wheelGestures"];
}

interface CarouselState {
  activeId?: string;
  activeIndex: number;
  canScrollNext: boolean;
  canScrollPrev: boolean;
  progress: number;
}

const MAX_WIDTH = 1440;
const GUTTER = [16, 32, 32];
const BREAKPOINTS = [768, 1200, 1341];

function getCarouselAlignmentPadding(viewSize: number) {
  const breakpointIndex = BREAKPOINTS.findIndex((breakpoint) => viewSize < breakpoint);
  const resolvedBreakpointIndex = breakpointIndex === -1 ? BREAKPOINTS.length - 1 : breakpointIndex;

  return Math.max(GUTTER[resolvedBreakpointIndex], (viewSize - MAX_WIDTH + GUTTER[resolvedBreakpointIndex] * 2) / 2);
}

export function useCarousel({ autoScroll = false, dragFree, loop, wheelGestures = false }: UseCarouselProps) {
  const [state, setState] = useState<CarouselState>({
    activeId: undefined,
    activeIndex: 0,
    canScrollNext: loop,
    canScrollPrev: loop,
    progress: 0,
  });

  const options = useMemo(
    () => ({
      loop,
      dragFree,
      align: loop ? "center" : (getCarouselAlignmentPadding as CarouselProps["align"]),
    }),
    [dragFree, loop],
  );

  const plugins = useMemo(() => {
    const emblaPlugins = [];

    if (autoScroll) {
      emblaPlugins.push(
        AutoScroll({
          startDelay: 0,
          speed: 0.5,
          ...(typeof autoScroll === "object" ? autoScroll : {}),
        }),
      );
    }

    if (wheelGestures) {
      emblaPlugins.push(WheelGesturesPlugin(typeof wheelGestures === "object" ? wheelGestures : {}));
    }

    return emblaPlugins;
  }, [autoScroll, wheelGestures]);

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const syncSelection = useCallback(
    (api: EmblaCarouselType) => {
      const currentIndex = api.selectedScrollSnap();
      const currentId = api.slideNodes()?.[currentIndex]?.id;

      setState((currentState) => ({
        ...currentState,
        activeId: currentId,
        activeIndex: currentIndex,
        canScrollPrev: loop || api.canScrollPrev(),
        canScrollNext: loop || api.canScrollNext(),
      }));
    },
    [loop],
  );

  const syncProgress = useCallback((api: EmblaCarouselType) => {
    setState((currentState) => ({
      ...currentState,
      progress: api.scrollProgress(),
    }));
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    syncSelection(emblaApi);
    syncProgress(emblaApi);

    emblaApi.on("reInit", syncSelection);
    emblaApi.on("select", syncSelection);
    emblaApi.on("reInit", syncProgress);
    emblaApi.on("scroll", syncProgress);

    return () => {
      emblaApi.off("reInit", syncSelection);
      emblaApi.off("select", syncSelection);
      emblaApi.off("reInit", syncProgress);
      emblaApi.off("scroll", syncProgress);
    };
  }, [emblaApi, syncProgress, syncSelection]);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return {
    activeId: state.activeId,
    activeIndex: state.activeIndex,
    canScrollNext: state.canScrollNext,
    canScrollPrev: state.canScrollPrev,
    emblaRef,
    handleNext,
    handlePrev,
    scrollTo: emblaApi?.scrollTo,
    progress: state.progress,
  };
}
